<script lang="ts">
  import { page } from '$app/stores';
  import { browser } from '$app/environment';
  import AlertCard from '$components/alert-card.svelte';
  import Button from '$components/ui/button.svelte';
  import { AUTH_DRAWER_IDS } from '$lib/constants';
  import { t } from '$lib/i18n';
  import { drawer } from '$lib/stores/drawer';
  import { user } from '$lib/stores/user';
  import { getAnchorFromCmsLink } from '$lib/utils/cms';
  import { wishlist, wishlistQuery } from '$lib/wishlist';
  import { onMount } from 'svelte';
  import RecentlyViewed from '$components/blocks/recently-viewed.svelte';
  import BottomSection from '$components/blocks/bottom-section.svelte';
  import ProductCard from '$components/product-card/product-card.svelte';
  import Skeleton from '$components/ui/skeleton.svelte';
  import { trimId } from '$lib/utils/shopify/productsId';
  import { buildPath } from '$lib/utils/paths';
  import { region } from '$lib/region';

  $: query = $wishlistQuery.getQuery({
    onSuccess: (newData) => {
      data = newData;
    }
  });
  let data = $query?.data;

  let showCopiedMessage = false;

  onMount(() => {
    $query.refetch();
  });

  $: productsInWishlist =
    $query.data?.filter((product) => product?.__typename === 'Product').length || 0;

  $: priciestItem = data?.reduce((acc, product) => {
    if (product?.__typename === 'Product') {
      const price = Number(product.priceRange.minVariantPrice.amount);
      const accPrice =
        acc?.__typename === 'Product' ? Number(acc.priceRange.minVariantPrice.amount) : 0;
      if (accPrice > price) {
        return product;
      }
    }
    return data ? data[0] : null;
  }, data[0]);

  $: webShareAPISupported = browser && typeof navigator.share !== 'undefined';

  let isShareLoading = false;

  const handleWebShareDesktop = async () => {
    isShareLoading = true;

    const productsList = data
      ?.filter((p) => p != null && p.__typename == 'Product')
      ?.map((p) => trimId(p.id))
      ?.join('_');

    const urlToShorten = $user?.firstName
      ? `/wishlist/${$region}/${productsList}/${$user?.firstName}?utm_medium=ShareWishlist&utm_source=Wishlist`
      : `/wishlist/${$region}/${productsList}?utm_medium=ShareWishlist&utm_source=Wishlist`;

    const text = new ClipboardItem({
      'text/plain': fetch(
        $buildPath({
          path: `/get-short-url?url=${encodeURIComponent(urlToShorten || '')}`
        })
      )
        .then((response) => response.text())
        .then((text) => {
          isShareLoading = false;
          return new Blob([new URL($buildPath({ path: text }), $page.url).toString()], {
            type: 'text/plain'
          });
        })
    });
    navigator.clipboard
      .write([text])
      .then(() => {
        showCopiedMessage = true;
        setTimeout(() => (showCopiedMessage = false), 1000);
      })
      .catch(() => {
        showCopiedMessage = false;
      });
  };

  const handleWebShareMobile = async () => {
    const ids = data
      ?.map((p) => {
        if (p?.__typename == 'Product') {
          return trimId(p.id);
        }
      })
      ?.join('_');

    const title = $user?.firstName
      ? $t('wishlist.share.link.title', { name: $user?.firstName })
      : $t('wishlist.share.link.title.default');
    const text = $t('shared_wishlist.subtitle');
    const name = $user?.firstName;
    const path = name ? `/wishlist/${name}/${ids}` : `/wishlist/${ids}`;
    const url = new URL($buildPath({ path: path }), $page.url).toString();

    if (webShareAPISupported) {
      try {
        navigator.share({ title, text, url });
      } catch (error) {
        webShareAPISupported = false;
      }
    }
  };
</script>

<div class="px-4 lg:px-8">
  <div class="container mx-auto mt-2 md:mt-1 mb-12 md:my-12">
    <div class="mb-4 md:mb-16">
      {#if $user === null}
        <div class="flex flex-row gap-2 w-full md:w-fit mr-0 ml-auto">
          <AlertCard message={$t('wishlist.save')} class="w-full" />
          <Button
            name={AUTH_DRAWER_IDS.LOGIN}
            variant="accent"
            on:click={() => {
              drawer.open(AUTH_DRAWER_IDS.LOGIN);
            }}>Login</Button
          >
        </div>
      {/if}
      <div class="flex flex-col md:flex-row justify-between md:items-center gap-2 mt-14 md:mt-5">
        <h1 class="text-4xl font-semibold">{$t('wishlist.title')}</h1>
        {#if ($wishlist ?? []).length > 0}
          <!-- Desktop -->
          {#if showCopiedMessage}
            <AlertCard icon="check-circle" message={$t('wishlist.share.copied')} size="md" />
          {:else}
            <Button
              variant="ghost"
              size="sm"
              icon={'share-favorites'}
              class="w-fit hidden md:flex"
              loading={isShareLoading}
              on:click={() => {
                handleWebShareDesktop();
              }}>{$t('wishlist.share')}</Button
            >
          {/if}

          <!-- Mobile -->
          <Button
            variant="ghost"
            size="sm"
            icon={'share-favorites'}
            class="w-full md:hidden"
            on:click={() => {
              handleWebShareMobile();
            }}>{$t('wishlist.share')}</Button
          >
        {/if}
      </div>
    </div>

    {#if ($wishlist ?? []).length}
      <p class="mb-4 text-primary/40">
        {$t('shop.n-products', { value: productsInWishlist.toString() })}
      </p>
    {/if}

    <div class="grid-cols-2 gap-2 grid lg:gap-4 lg:grid-cols-[repeat(auto-fill,minmax(280px,1fr))]">
      {#if $wishlist === undefined || data === undefined}
        {#each [1, 2, 3, 4] as _}
          <Skeleton class="aspect-[183/304] lg:aspect-[332/480] rounded-md" />
        {/each}
      {:else}
        {#each data as product, i (product?.__typename === 'Product' ? product.id : i)}
          {#if product && product.__typename === 'Product'}
            <ProductCard {product} />
          {/if}
        {:else}
          <div>
            <p class="text-xl font-semibold">{$t('wishlist.empty.title')}</p>
            <p class="mt-1 opacity-40">{$t('wishlist.empty.description')}</p>
            {#if $page.data.configuration.content.cart_empty_cta?.[0].url}
              {@const { href, rel, target } = $getAnchorFromCmsLink(
                $page.data.configuration.content.cart_empty_cta[0].url
              )}
              <Button
                name={$page.data.configuration.content.cart_empty_cta[0].text}
                class="mt-4"
                variant="accent"
                as="a"
                {href}
                {rel}
                {target}>{$page.data.configuration.content.cart_empty_cta[0].text}</Button
              >
            {/if}
          </div>
        {/each}
      {/if}
    </div>
  </div>
</div>
{#if priciestItem && priciestItem.__typename === 'Product'}
  <BottomSection
    block={{
      _uid: `fake-recommendations`,
      component: 'bottom-section',
      title: $t('pdp.recommendations.you-may-also-like'),
      content: [
        {
          _uid: `fake-recommendations-content`,
          automatic_recommendations: 'RELATED',
          component: 'product-recommendations'
        }
      ]
    }}
    referenceProductId={priciestItem.id}
  />
{/if}
<RecentlyViewed
  block={{
    _uid: `fake-recently-viewed`,
    amount: '10',
    component: 'recently-viewed'
  }}
/>
